import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CreateAccount from './CreateAccount.js';
import { userSession } from '../utils/userSession.js';
import { renderTurnstile, validateTurnstileToken, cleanupTurnstile } from '../utils/turnstile.js';
import ResetPassword from './ResetPassword.js';
import ForgotPassword from './ForgotPassword.js';
import SocialAuthButtons from './SocialAuthButtons.js';

export default function LoginPanel({ isOpen, onClose, user, onLogin, onOpenDashboard, message }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isTurnstileValid, setIsTurnstileValid] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [userId, setUserId] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (isOpen) {
      renderTurnstile(handleTurnstileToken);
    }
    
    // Cleanup when component unmounts or dialog closes
    return () => {
      cleanupTurnstile();
    };
  }, [isOpen]); // Re-run when dialog opens/closes

  const handleTurnstileToken = async (token) => {
    setTurnstileToken(token);
    const isValid = await validateTurnstileToken(token);
    setIsTurnstileValid(isValid);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!isTurnstileValid || !turnstileToken) {
      setError('Please complete the security check');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, turnstileToken }),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.usingTempPassword || data.passwordResetRequired) {
          setUserId(data.userId);
          setShowResetPassword(true);
          setLoading(false);
          setError('Please change your temporary password');
          return;
        }
        
        userSession.setUser(data, rememberMe);
        onLogin(data);
        onClose();
        setEmail('');
        setPassword('');
        setTurnstileToken('');
        setIsTurnstileValid(false);
        onOpenDashboard();
      } else {
        setError(data.message || 'Failed to login');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSuccess = (data) => {
    userSession.setUser(data, rememberMe);
    onLogin(data);
    onClose();
    setEmail('');
    setPassword('');
    onOpenDashboard();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <div 
        className={`fixed inset-y-0 right-0 w-96 bg-gray-50 shadow-lg transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } z-50`}
      >
        <div className="relative h-full overflow-y-auto">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="flex flex-col px-6 pt-8">
            <div className="mx-auto w-full max-w-sm">
              {user ? (
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              ) : (
                <>
                  <img
                    alt="DriftCal"
                    src="/driftcal_512_black.png"
                    className="mx-auto h-10 w-auto"
                  />
                  <h2 className="mt-4 text-center text-xl font-bold text-gray-900">
                    Sign in to your account
                  </h2>
                 
                  <div className="mt-6 w-full max-w-sm mx-auto">
                    <SocialAuthButtons
                      active={isOpen && !showCreateAccount}
                      disabled={loading}
                      onSuccess={handleSocialSuccess}
                    />
                    <form className="space-y-6" onSubmit={handleSignIn}>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                          Email address
                        </label>
                        <div className="mt-1">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="username"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between">
                          <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                            Password
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-sm font-semibold text-slate-900 hover:text-slate-700"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <div className="mt-1">
                          <input
                            id="password"
                            name="password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            id="remember-me"
                            name="remember-me"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-slate-900 focus:ring-slate-600"
                          />
                          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                            Remember me
                          </label>
                        </div>
                      </div>

                      <div 
                        id="turnstile-widget" 
                        className="flex justify-center touch-manipulation"
                        style={{
                          minHeight: '65px',
                          touchAction: 'manipulation',
                          cursor: 'pointer'
                        }}
                      ></div>

                      {error && (
                        <div className="text-sm text-red-600">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading || !isTurnstileValid}
                        className="w-full rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:bg-slate-400"
                      >
                        {loading ? 'Signing in...' : 'Sign in'}
                      </button>
                    </form>
                    
                    <p className="mt-6 text-center text-sm text-gray-500">
                      Don't have an account?{' '}
                      <button 
                        onClick={() => setShowCreateAccount(true)}
                        className="font-semibold text-slate-900 hover:text-slate-700"
                      >
                        Create one
                      </button>
                    </p>
                    {message && (
                    <p className="mt-2 text-center text-sm text-red-600">
                      {message}
                    </p>
                  )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateAccount 
        isOpen={showCreateAccount}
        onClose={() => {
          setShowCreateAccount(false);
          setError('');
          setEmail('');
          setPassword('');
        }}
        onLogin={(userData) => {
          setShowCreateAccount(false);
          setError('');
          setEmail('');
          setPassword('');
          onLogin(userData);
          onClose();
        }}
      />

      <ResetPassword
        isOpen={showResetPassword}
        onClose={() => {
          setShowResetPassword(false);
          setUserId(null);
          setEmail('');
          setPassword('');
        }}
        userId={userId}
        onSuccess={() => {
          setShowResetPassword(false);
          setUserId(null);
          setEmail('');
          setPassword('');
          onClose();
        }}
      />

      <ForgotPassword
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setError('');
        }}
      />
    </>
  );
} 